import React, { useEffect, useCallback, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import CTA from '../Button';
import { CTA_LIST } from '../../constants/CtaList';
import { Container, AddressAutoCompleteContaniner, Overlay } from './AddressInput.styles';
import AddressAutoComplete from '../AddressAutoComplete';
import { selectDealersList, setUserSelectedLocation } from '../../reducers/dealersListReducer';
import { Coordinates } from '../../types/locations';
import useFocusTrap from '../../hooks/useFocusTrap';
import reverseGeocode from '../../utils/reverseGeocode';

interface AddressInputProps {
  onChange: () => void;
}

const AddressInput: React.FC<AddressInputProps> = ({ onChange }) => {
  const dispatch = useDispatch();
  const [geolocationLoading, setGeolocationLoading] = useState<boolean>(false);
  const { centerLocation, isEdit } = useSelector(selectDealersList);
  const isModalOpened = useMemo(() => {
    return !isEdit;
  }, [isEdit]);

  const FocusTrapRef = useFocusTrap(isModalOpened);

  const handleDeclineEdit = useCallback(
    (e: React.MouseEvent) => {
      if (e.target !== e.currentTarget) return;
      if (centerLocation && isModalOpened) {
        onChange();
      }
    },
    [centerLocation, isModalOpened, onChange],
  );

  const handleKeyPress = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (centerLocation && isModalOpened) {
          onChange();
        }
      }
    },
    [centerLocation, isModalOpened, onChange],
  );

  useEffect(() => {
    if (!isModalOpened) return undefined;

    document.addEventListener('keydown', handleKeyPress);

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleKeyPress, isModalOpened]);

  const handleLocationClick = async (position: GeolocationPosition) => {
    const { latitude, longitude } = position.coords;
    const coords: Coordinates = {
      latitude,
      longitude,
    };
    const reversedPlace = await reverseGeocode(coords);
    dispatch(setUserSelectedLocation({ place: reversedPlace, isGeoLocation: true }));
    setGeolocationLoading(false);
  };

  const handleSetLoadingTrue = () => {
    setGeolocationLoading(true);
  };

  const handleFailToGetLocation = () => {
    console.log('Unable to retrieve your location');
    setGeolocationLoading(false);
  };

  return (
    <Overlay onClick={handleDeclineEdit} tabIndex={0}>
      <Container ref={FocusTrapRef}>
        <AddressAutoCompleteContaniner>
          <AddressAutoComplete />
        </AddressAutoCompleteContaniner>
        <div>
          <CTA
            buttonType={CTA_LIST.GPS_CTA}
            onSetLoadingTrue={handleSetLoadingTrue}
            onGetLocation={handleLocationClick}
            failToGetLocation={handleFailToGetLocation}
            loading={geolocationLoading}
          />
        </div>
      </Container>
    </Overlay>
  );
};

export default AddressInput;
